import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * Android hardware back button → React Router history.
 *
 * Default Capacitor behavior is to call `App.exitApp()` any time the user
 * presses Back, even if there is meaningful in-app history. That is
 * jarring for a full-screen SPA. We override it here:
 *
 *   1. If any element with `data-back-dismiss` is visible on the page
 *      (contact editor, invitation sheet, modal, drawer), simulate a
 *      click on its close button first — the OS Back should close
 *      overlays before it navigates screens.
 *   2. Otherwise, if the router has meaningful history (i.e. the current
 *      route is not the app root and there is a real previous entry),
 *      call `navigate(-1)`.
 *   3. Only when we're truly at the root do we let Capacitor exit the
 *      app (so hitting Back once from `/dashboard` still exits, matching
 *      normal Android launcher behavior).
 *
 * Mount once inside `<BrowserRouter>` — do NOT mount multiple copies or
 * you'll get duplicate handlers.
 */
const ROOT_PATHS = new Set(["/", "/dashboard", "/group"]);

export default function useAndroidBackButton() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let sub;
    (async () => {
      sub = await CapacitorApp.addListener("backButton", ({ canGoBack }) => {
        // Priority 1 — close any open overlay marked with `data-back-dismiss`.
        // The element's `data-back-dismiss` value is a CSS selector for the
        // *close button* to click (e.g. `data-back-dismiss="[data-testid=close-sheet]"`).
        const overlay = document.querySelector("[data-back-dismiss]");
        if (overlay) {
          const target = overlay.getAttribute("data-back-dismiss");
          const btn = target ? document.querySelector(target) : null;
          if (btn && typeof btn.click === "function") {
            btn.click();
            return;
          }
          // Fallback: dispatch a synthetic Escape keydown that most modals
          // already listen for.
          document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
          return;
        }
        // Priority 2 — pop router history if we're not at a root screen.
        const atRoot = ROOT_PATHS.has(location.pathname);
        if (!atRoot && (canGoBack || window.history.length > 1)) {
          navigate(-1);
          return;
        }
        // Priority 3 — actually exit the app.
        try { CapacitorApp.exitApp(); } catch { /* web fallback: no-op */ }
      });
    })();
    return () => { try { sub?.remove?.(); } catch {} };
  }, [navigate, location.pathname]);
}
